import { useState } from "react";
import { Lock, FileText, Download, Hash, Calendar, Shield } from "lucide-react";
import WaitlistGate, { hasArmadaAccess, triggerDownload } from "@/components/nessa/WaitlistGate";
import { toast } from "sonner";

const META = [
  { icon: Hash, label: "Ref", value: "ARMADA-VOL-0" },
  { icon: Calendar, label: "Issued", value: "2026.02" },
  { icon: Shield, label: "Clearance", value: "25D · SOVEREIGN" },
];

export default function DigitalVault() {
  const [gateOpen, setGateOpen] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    if (hasArmadaAccess()) {
      triggerDownload();
      toast.success("Armada Manifesto (Vol 0) deployed", {
        description: "Welcome back, sovereign.",
      });
      return;
    }
    setGateOpen(true);
  };

  return (
    <section id="digital-vault" data-testid="digital-vault">
      <div className="mb-7">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-violet animate-pulse-violet" />
          <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-violet-soft">
            Digital Vault
          </span>
        </div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-white tracking-tight">
          Classified Assets · Sealed Archive
        </h2>
        <p className="mt-3 max-w-2xl text-white/55 leading-relaxed">
          The vault holds the doctrine. Each volume is signed, timestamped, and
          cleared for sovereign access.
        </p>
      </div>

      <div
        className="relative rounded-2xl p-8 sm:p-10 bg-[#080808] border border-white/8 tactical-corner overflow-hidden"
        data-testid="armada-manifesto-card"
      >
        {/* Stamp */}
        <div className="absolute top-7 right-6 sm:top-9 sm:right-10 pointer-events-none">
          <span className="classified-stamp">Classified · Sovereign</span>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-stretch gap-8">
          {/* Document mock */}
          <div className="md:w-56 shrink-0">
            <div className="aspect-[3/4] rounded-md border border-gold/40 bg-gradient-to-b from-black to-obsidian-surface relative overflow-hidden">
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(212,175,55,0.10),transparent_40%)]" />
              <div className="absolute inset-0 p-4 flex flex-col justify-between">
                <div>
                  <FileText className="w-5 h-5 text-gold" />
                  <div className="mt-3 font-mono text-[8px] tracking-[0.3em] uppercase text-white/40">
                    Vol · 00
                  </div>
                  <div className="mt-2 font-heading text-base leading-tight text-white">
                    The Armada Manifesto
                  </div>
                </div>
                <div>
                  <div className="h-px bg-gold/30 mb-3" />
                  <div className="font-mono text-[8px] tracking-[0.24em] uppercase text-gold/80">
                    Sovereign Lifestyle Hub
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gold/30" />
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-gold" />
              <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold/90">
                Asset 001 · Encrypted Manifesto
              </span>
            </div>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl text-white tracking-tight">
              Armada Manifesto <span className="gold-text">(Vol 0)</span>
            </h3>
            <p className="mt-3 text-white/60 leading-relaxed max-w-xl">
              The opening doctrine. Foundational principles of sovereign command,
              the 80/10/10 rescue logic, and the operator&apos;s covenant. Drafted for
              the Armada · sealed for the few.
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {META.map(({ icon: Icon, label, value }) => (
                <div
                  key={label}
                  className="rounded-md border border-white/8 bg-black/60 p-3"
                  data-testid={`vault-meta-${label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-1.5 text-white/40">
                    <Icon className="w-3 h-3" />
                    <span className="font-mono text-[9px] tracking-[0.24em] uppercase">
                      {label}
                    </span>
                  </div>
                  <div className="mt-1 font-mono text-[11px] tracking-[0.16em] uppercase text-white">
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleClick}
                data-testid="manifesto-download-btn"
                className="group inline-flex items-center gap-2 h-11 px-6 rounded-full bg-gold hover:bg-gold-soft text-obsidian font-mono text-[11px] tracking-[0.22em] uppercase shadow-[0_8px_28px_-8px_rgba(212,175,55,0.6)] transition-colors"
              >
                <Lock className="w-4 h-4" /> Request Sovereign Access
              </button>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">
                · PDF · Sealed · 1.4 MB · Email-Gated
              </div>
            </div>
          </div>
        </div>

        {/* footer barcode-ish */}
        <div className="mt-8 pt-5 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {Array.from({ length: 28 }).map((_, i) => (
              <span
                key={i}
                className="block bg-white/30"
                style={{
                  width: 2,
                  height: 14,
                  opacity: (i * 37) % 5 === 0 ? 1 : 0.4,
                }}
              />
            ))}
          </div>
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">
            Auth ID · 7FA-DELTA-NESSA
          </span>
        </div>
      </div>

      <WaitlistGate open={gateOpen} onOpenChange={setGateOpen} />
    </section>
  );
}
